/**
 * Splitting a reworded phrase back over the scenes the original phrase spanned.
 *
 * A reword whose word count differs from the phrase it replaces is re-segmented by the backend,
 * so the new text has to be handed over as one run per scene. Putting the whole run on the first
 * scene leaves every other scene it crossed with no words, and the correction save deletes
 * word-less scenes — which is how a whole-transcript reword collapses a scene split into one scene.
 */

// A word that closes a sentence, allowing for a trailing quote or bracket.
const SENTENCE_END_REGEX = /[.!?]["'”’)\]]*$/;
// A word that closes a clause; the fallback when no sentence end is near the cut.
const CLAUSE_END_REGEX = /[,;:—–-]["'”’)\]]*$/;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Move a proposed cut to the nearest sentence end, or failing that the nearest clause end, so
 * scene boundaries keep landing between sentences (or at least between clauses) rather than
 * mid-phrase. A cut of `c` means `words[c - 1]` ends the segment. The snap is limited to
 * `earliest..latest` and to a drift of about half the segment, so a scene is never starved or
 * doubled just to reach a full stop.
 */
const snapToBreak = (
    words: string[],
    proposedCut: number,
    segmentStart: number,
    earliest: number,
    latest: number,
): number => {
    const maxDrift = Math.max(1, Math.floor((proposedCut - segmentStart) / 2));
    const nearestCutEndingWith = (breakRegex: RegExp): number | undefined => {
        let best: number | undefined;
        let bestDistance = Infinity;
        for (let cut = earliest; cut <= latest; cut++) {
            const distance = Math.abs(cut - proposedCut);
            if (distance > maxDrift || distance >= bestDistance) {
                continue;
            }
            if (breakRegex.test(words[cut - 1])) {
                best = cut;
                bestDistance = distance;
            }
        }
        return best;
    };
    return (
        nearestCutEndingWith(SENTENCE_END_REGEX) ??
        nearestCutEndingWith(CLAUSE_END_REGEX) ??
        proposedCut
    );
};

/**
 * Share `words` out over consecutive segments, in order, sized in proportion to
 * `originalCounts` (how many of the replaced words each segment held). Every segment receives at
 * least one word, so `words.length` must be at least `originalCounts.length`.
 */
export const distributeWordsAcrossSegments = (
    words: string[],
    originalCounts: number[],
): string[][] => {
    const segmentCount = originalCounts.length;
    if (words.length < segmentCount) {
        throw new Error(
            `cannot spread ${words.length} word(s) over ${segmentCount} segments with at least one word each`,
        );
    }
    const totalOriginal = originalCounts.reduce((sum, count) => sum + count, 0);

    const shares: string[][] = [];
    let cursor = 0;
    let originalSoFar = 0;
    for (let i = 0; i < segmentCount - 1; i++) {
        originalSoFar += originalCounts[i];
        const remainingSegments = segmentCount - i - 1;
        const earliest = cursor + 1;
        const latest = words.length - remainingSegments;
        const proportionalCut = clamp(
            Math.round((words.length * originalSoFar) / totalOriginal),
            earliest,
            latest,
        );
        const cut = snapToBreak(words, proportionalCut, cursor, earliest, latest);
        shares.push(words.slice(cursor, cut));
        cursor = cut;
    }
    shares.push(words.slice(cursor));
    return shares;
};
